import React, { useCallback } from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import { FileThumbnail, fileData } from "@onair/minimal-ui/file-thumbnail";
import { Field, schemaHelper } from "@onair/minimal-ui/hook-form";
import { WizardSchemaType } from "../../index";
import { useFieldArray, UseFormReturn, useWatch } from "react-hook-form";
import { Iconify } from "@onair/minimal-ui/iconify";
import Grid from "@mui/material/Grid";
import TextMaxLine from "@onair/ui/text-max-line";

interface Props {
	methods: UseFormReturn<WizardSchemaType>;
}

const DocContent = ({ methods }: Props) => {
	const { control, setValue } = methods;

	const { remove } = useFieldArray({
		control,
		// @ts-ignore
		name: "stepResource.docs",
	});

	const docs =
		useWatch({
			name: "stepResource.docs",
			control,
		}) || [];

	const handleDropDocs = useCallback(
		async (acceptedFiles: File[]) => {
			setValue("stepResource.docs", [...(docs ?? []), ...acceptedFiles], {
				shouldValidate: true,
				shouldDirty: true,
			});
		},
		[docs],
	);

	return (
		<Stack spacing={2}>
			<Grid container spacing={2}>
				{docs?.length === 0 && (
					<Grid item xs={12}>
						<Typography
							display="block"
							align="center"
							variant="caption"
							color="text.secondary"
						>
							Chưa có tài liệu nào được tải lên
						</Typography>
					</Grid>
				)}
				{(docs ?? []).map((file, index) => {
					const { name } = fileData(file);
					return (
						<Grid item key={index} xs={12} md={12 / 5}>
							<Box
								position="relative"
								height="100%"
								display="flex"
								sx={{
									flexDirection: {
										xs: "row",
										md: "column",
									},
									gap: {
										xs: 1,
										md: 0.5,
									},
									alignItems: {
										xs: "center",
										md: "unset",
									},
								}}
								borderRadius={1}
								bgcolor="grey.100"
								py={1.5}
								px={1}
							>
								<Box display="flex" alignSelf="center">
									<FileThumbnail file={file} />
								</Box>
								<TextMaxLine
									line={1}
									flexGrow={1}
									fontSize="10px"
									fontWeight={500}
									variant="caption"
									color="text.primary"
									sx={{
										wordWrap: "break-word",
									}}
								>
									{name}
								</TextMaxLine>
								<Box
									bgcolor="rgba(0, 0, 0, 0.48)"
									width={16}
									height={16}
									borderRadius="50%"
									display="flex"
									alignItems="center"
									justifyContent="center"
									zIndex={1}
									top={8}
									right={8}
									color="white"
									sx={{
										position: {
											xs: "unset",
											md: "absolute",
										},
										cursor: "pointer",
									}}
									onClick={() => remove(index)}
								>
									<Iconify icon="mingcute:close-line" width={10.2} />
								</Box>
							</Box>
						</Grid>
					);
				})}
			</Grid>
			<Stack
				justifyContent="end"
				alignItems="center"
				direction="row"
				spacing={1.5}
			>
				{docs?.length > 0 && (
					<Button
						onClick={() =>
							setValue("stepResource.docs", [], {
								shouldValidate: true,
							})
						}
						variant="outlined"
						color="inherit"
					>
						Xóa hết
					</Button>
				)}
				<Field.UploadBox
					onDrop={handleDropDocs}
					name="stepResource.docs"
					multiple
					error={false}
					maxSize={5 * 1025 * 1024}
					accept={{
						"application/pdf": [],
						"application/msword": [],
						"application/vnd.ms-powerpoint": [],
						"application/vnd.ms-excel": [],
						"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
							[],
					}}
					sx={{
						width: "unset",
						height: "unset",
						backgroundColor: "unset",
					}}
					onRemove={(inputFile) =>
						setValue(
							"stepResource.docs",
							docs?.filter((file) => file !== inputFile) ?? [],
							{ shouldValidate: true },
						)
					}
					placeholder={
						<Button
							startIcon={<Iconify icon="eva:cloud-upload-fill" width={20} />}
							variant="outlined"
							color="primary"
						>
							Tải lên
						</Button>
					}
				/>
			</Stack>
		</Stack>
	);
};

export default DocContent;
